// src/components/ModalProyecto.jsx
import React, { useState, useRef, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { formatShortName } from "../hooks/useUserAvatar";
import Modal from "react-modal";
import Avatar from "./Avatar";
import { tagStyles } from "../hooks/useAddProductConfig";

/* ─── Estilo base del modal ─── */
const modalStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    width: "90%",
    maxWidth: 820,
    maxHeight: "92vh",
    overflowX: "hidden",
    overflowY: "auto",
    transform: "translate(-50%, -50%)",
    padding: 32,
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    boxShadow: "0 8px 24px rgba(0,0,0,.15)",
    fontFamily: "Inter, sans-serif",
  },
  overlay: { backgroundColor: "rgba(0,0,0,.45)", zIndex: 1008 },
};

/* ─── Estilos reutilizables ─── */
const controlBase = {
  width: "100%",
  minWidth: 0,
  border: "1px solid #d1d5db",
  borderRadius: 6,
  background: "#F8FAFB",
  padding: "10px 12px",
  fontFamily: "Inter, sans-serif",
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box",
};
const label = { display: "block", fontWeight: 600, marginBottom: 12 };
const priorities = ["Alta", "Normal", "Baja"];

const usersRow = { display: "flex", alignItems: "center", gap: 16 };

// botón "Add member" con tu estilo
const inviteBtn = {
  background: "#fff",
  border: "1px solid #d1d5db",
  borderRadius: 6,
  padding: "6px 14px",
  fontSize: 14,
  cursor: "pointer",
};

// dropdown
const inviteDropdown = {
  position: "absolute",
  top: "100%",
  left: 0,
  marginTop: 6,
  width: 260,
  background: "#fff",
  border: "1px solid #E5E7EB",
  borderRadius: 12,
  boxShadow: "0 20px 40px rgba(2,6,23,.12)",
  zIndex: 10,
  padding: 8,
};

const inviteItem = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "8px 10px",
  border: 0,
  background: "transparent",
  borderRadius: 8,
  cursor: "pointer",
};

const dropdownEmpty = { fontSize: 13, color: "#94a3b8", padding: "8px 10px" };

// Avatar overlap
const overlapAvatar = (idx) => ({
  width: 32,
  height: 32,
  borderRadius: "50%",
  boxShadow: "0 0 0 1px #fff",
  marginLeft: idx === 0 ? 0 : -10,
  position: "relative",
  zIndex: idx + 1,
});

// "+N" badge
const badgeStyle = {
  width: 32,
  height: 32,
  borderRadius: "50%",
  background: "#2563eb",
  color: "#fff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 12,
  fontWeight: 600,
  boxShadow: "0 0 0 1px #fff",
  marginLeft: -8,
  position: "relative",
  zIndex: 999,
};

const ModalProyecto = ({
  isOpen,
  closeModal,
  owner,
  teamOwner,
  projectName,
  setProjectName,
  description,
  setDescription,
  usuarios = [],
  assignees = [],
  tags = [],
  setTags,
  onAddMember,
  priority,
  setPriority,
  startDate,
  setStartDate,
  dueDate,
  setDueDate,
  selectedGroupId,
  setSelectedGroupId,
  notifyMode,
  setNotifyMode,
  notifyQty,
  setNotifyQty,
  notifyUnit,
  setNotifyUnit,
  handleSaveProject,
  grupos,
  modo = "crear",
}) => {
  const { user: authUser, firstName, lastName, avatarUrl } = useAuth();
  const [members, setMembers] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const inviteRef = useRef(null);
  const [tagValue, setTagValue] = useState("");
  const [groupLocalId, setGroupLocalId] = useState(selectedGroupId);

  useEffect(() => {
    if (isOpen) {
      setGroupLocalId(selectedGroupId || grupos[0]?.id || "");
    }
  }, [isOpen, selectedGroupId, grupos]);

  // cerrar dropdown si hacen click fuera
  useEffect(() => {
    const onDocClick = (e) => {
      if (inviteRef.current && !inviteRef.current.contains(e.target))
        setInviteOpen(false);
    };
    if (inviteOpen) document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [inviteOpen]);

  // al abrir el modal: muestra SOLO el creador (o los que vengan si ya hay asignados)
  useEffect(() => {
    if (!isOpen) return;

    if (assignees?.length) {
      const seen = new Set();
      const uniq = assignees.filter((u) => {
        const id = u?.id ?? u?.uid;
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      });
      setMembers(uniq);
      return;
    }

    // si no hay assignees, inicia solo con el creador (usuario actual)
    if (authUser) {
      setMembers([
        {
          id: authUser.uid,
          nombre: firstName,
          apellido: lastName,
          email: authUser.email,
          avatarUrl,
        },
      ]);
    } else {
      setMembers([]);
    }
  }, [isOpen, assignees, authUser, firstName, lastName, avatarUrl]);

  const addTag = (raw) => {
    const v = (raw || "").trim();
    if (!v) return;
    if (tags.includes(v)) return;
    if (tags.length >= 5) return; // máximo 5
    setTags((t) => [...t, v]);
  };

  const onTagKeyDown = (e) => {
    if ((e.key === "Enter" || e.key === ",") && tagValue.trim() !== "") {
      e.preventDefault();
      addTag(tagValue);
      setTagValue("");
    } else if (e.key === "Backspace" && tagValue === "" && tags.length) {
      // borrar el último chip si el input está vacío
      setTags((t) => t.slice(0, -1));
    }
  };

  const removeTag = (idx) => setTags((t) => t.filter((_, i) => i !== idx));

  const addMemberById = (id) => {
    if (!id) return;

    // ya está en la lista?
    if (members.some((m) => (m.id ?? m.uid) === id)) {
      setInviteOpen(false);
      return;
    }

    // buscar por id o uid
    const raw = usuarios.find((u) => (u.id ?? u.uid) === id);
    if (!raw) {
      setInviteOpen(false);
      return;
    }

    // normalizar el shape del usuario
    const found = {
      id: raw.id ?? raw.uid,
      nombre: raw.nombre ?? raw.firstName ?? "",
      apellido: raw.apellido ?? raw.lastName ?? "",
      email: raw.email ?? "",
      avatarUrl: raw.avatarUrl ?? "/img/avatar2.jpg",
    };

    // actualizar UI local
    setMembers((prev) => [...prev, found]);

    // sincronizar con el padre (SidebarTareas) si lo pasaste
    if (typeof onAddMember === "function") {
      onAddMember(found);
    }

    setInviteOpen(false);
  };

  // cuántos extra para el +N
  const extra = Math.max(members.length - 3, 0);

  return (
    <Modal isOpen={isOpen} onRequestClose={closeModal} style={modalStyles}>
      {/* Header */}
      <div style={{ justifyContent: "space-between" }}>
        <h2 style={{ margin: 0, color: "#44566C" }}>
          {modo === "editar" ? "Editar proyecto" : "Agregar nuevo proyecto"}
        </h2>
        <button
          onClick={closeModal}
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            background: "#EAEDF0",
            color: "#44566C",
            borderRadius: "50%",
            height: 32,
            width: 32,
            border: "none",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 0,
            lineHeight: 0,
            fontSize: 16,
          }}
        >
          <i className="bi bi-x-lg" />
        </button>
      </div>

      {/* Grid: ahora 2fr / 1fr */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "2fr 1fr",
          columnGap: 40,
          rowGap: 24,
          marginTop: 28,
        }}
      >
        {/* Columna izquierda */}
        <div>
          {/* Title */}
          <div style={{ marginBottom: 24 }}>
            <label style={label}>Título</label>
            <input
              type="text"
              placeholder="Agrega el título del proyecto"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              style={controlBase}
            />
          </div>

          {/* Description */}
          <div style={{ marginBottom: 24 }}>
            <label style={label}>Descripción</label>
            <div
              style={{
                border: "1px solid #d1d5db",
                borderRadius: 6,
                overflow: "hidden",
              }}
            >
              <textarea
                rows={8}
                placeholder="Escribe una descripción aqui"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                style={{
                  ...controlBase,
                  border: "none",
                  borderRadius: 0,
                  resize: "vertical",
                  minHeight: 178,
                  background: "#F8FAFB",
                }}
              />
            </div>
          </div>

          {/* Etiquetas */}
          <div style={{ marginBottom: 24 }}>
            <label style={label}>Etiquetas</label>
            <div style={tagStyles.tagsBox}>
              {tags.map((tag, i) => (
                <div key={`${tag}-${i}`} style={tagStyles.tagChip(i)}>
                  {tag}
                  <span style={tagStyles.tagClose} onClick={() => removeTag(i)}>
                    <i className="bi bi-x"></i>
                  </span>
                </div>
              ))}
              {tags.length < 5 && (
                <input
                  style={tagStyles.tagInput}
                  placeholder="Escribe un tag y presiona Enter…"
                  value={tagValue}
                  onChange={(e) => setTagValue(e.target.value)}
                  onKeyDown={onTagKeyDown}
                />
              )}
            </div>
          </div>
        </div>

        {/* Columna derecha */}
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {/* Assignees */}
          <div>
            <label style={label}>Asignar Usuarios</label>

            <div ref={inviteRef} style={{ position: "relative" }}>
              <div style={{ ...usersRow }}>
                {/* Avatares seleccionados */}
                <div style={{ display: "flex", position: "relative" }}>
                  {members.slice(0, 3).map((u, idx) => (
                    <div key={u.id || idx} style={overlapAvatar(idx)}>
                      <Avatar userId={u.id} size={32} />
                    </div>
                  ))}
                  {extra > 0 && (
                    <span style={{ ...badgeStyle, marginLeft: -10 }}>
                      +{extra}
                    </span>
                  )}
                </div>

                {/* Botón para abrir/cerrar dropdown */}
                <button
                  type="button"
                  onClick={() => setInviteOpen((v) => !v)}
                  style={inviteBtn}
                >
                  + Agregar
                </button>
              </div>

              {/* Dropdown de usuarios (los que NO están ya en members) */}
              {inviteOpen && (
                <div style={inviteDropdown}>
                  {usuarios.filter((u) => !members.some((m) => m.id === u.id))
                    .length === 0 ? (
                    <div style={dropdownEmpty}>Todos asignados</div>
                  ) : (
                    usuarios
                      .filter((u) => !members.some((m) => m.id === u.id))
                      .map((u) => (
                        <button
                          key={u.id}
                          type="button"
                          style={inviteItem}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.background = "#F8FAFC")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.background = "transparent")
                          }
                          onClick={() => addMemberById(u.id)}
                        >
                          <div
                            style={{
                              width: 24,
                              height: 24,
                              borderRadius: "50%",
                              overflow: "hidden",
                            }}
                          >
                            <Avatar userId={u.id} size={24} />
                          </div>
                          <span style={{ fontSize: 14, color: "#0f172a" }}>
                            {formatShortName(u)}
                          </span>
                        </button>
                      ))
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Priority */}
          <div>
            <label style={label}>Prioridad</label>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                columnGap: 14,
                rowGap: 8,
              }}
            >
              {priorities.map((p) => (
                <label
                  key={p}
                  style={{ display: "flex", alignItems: "center", gap: 4 }}
                >
                  <input
                    type="radio"
                    name="priority"
                    checked={priority === p}
                    onChange={() => setPriority(p)}
                  />
                  {p}
                </label>
              ))}
            </div>
          </div>

          {/* Dates */}
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
          >
            <div>
              <label style={label}>Fecha Inicio</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={controlBase}
              />
            </div>
            <div>
              <label style={label}>Ficha Final</label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                style={controlBase}
              />
            </div>
          </div>

          {/* Category */}
          <div>
            <label style={label}>Grupo</label>
            {grupos.length > 0 ? (
              <select
                value={groupLocalId || ""}
                onChange={(e) => {
                  setGroupLocalId(e.target.value); // estado local
                  setSelectedGroupId(e.target.value); // ← ACTUALIZA TAMBIÉN EL PADRE !!
                }}
                style={controlBase}
              >
                <option value="" disabled>
                  — Selecciona un grupo —
                </option>
                {grupos.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            ) : (
              <div
                style={{
                  ...controlBase,
                  background: "#f9fafb",
                  color: "#6b7280",
                }}
              >
                No hay grupos aún
              </div>
            )}
          </div>

          {/* Deadline notification */}
          <div>
            <label style={label}>Notificación de plazo</label>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 70px 1fr",
                gap: 12,
              }}
            >
              <select
                value={notifyMode}
                onChange={(e) => setNotifyMode(e.target.value)}
                style={controlBase}
              >
                <option value="">Alerta</option>
                <option value="Email">Notificación</option>
              </select>
              <input
                type="number"
                min={1}
                value={notifyQty}
                onChange={(e) => setNotifyQty(e.target.value)}
                style={controlBase}
              />
              <select
                value={notifyUnit}
                onChange={(e) => setNotifyUnit(e.target.value)}
                style={controlBase}
              >
                <option value="Days">Días</option>
                <option value="Hours">Horas</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{ display: "flex", gap: 16, marginTop: 36 }}>
        <button
          onClick={() => handleSaveProject(groupLocalId)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            background: "#2563eb",
            color: "#fff",
            border: 0,
            borderRadius: 8,
            padding: "12px 26px",
            fontSize: 14,
            fontWeight: 500,
            cursor: "pointer",
            boxShadow: "0 2px 6px rgba(0,0,0,.1)",
          }}
        >
          <i
            className={`bi bi-${modo === "editar" ? "check-lg" : "plus-lg"}`}
          />
          {modo === "editar" ? "Actualizar" : "Agregar"}
        </button>
        <button
          onClick={closeModal}
          style={{
            background: "#fff",
            border: "1px solid #d1d5db",
            borderRadius: 8,
            padding: "12px 26px",
            fontSize: 14,
            cursor: "pointer",
          }}
        >
          Cancelar
        </button>
      </div>
    </Modal>
  );
};

export default ModalProyecto;
